import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Loader2, ShieldCheck } from "lucide-react";
import { Logo } from "@/components/Logo";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

export default function Register() {
  const { setUser } = useAuth();
  const navigate = useNavigate();
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    try {
      const { data } = await api.post("/auth/register", { name, email, password });
      setUser(data.user);
      toast.success("Your account is ready");
      navigate("/dashboard");
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  const googleLogin = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/dashboard";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <div className="min-h-[calc(100vh-68px)] grid lg:grid-cols-2">
      <div className="flex items-center justify-center p-6 sm:p-12 bg-parchment-50 order-2 lg:order-1">
        <div className="w-full max-w-md animate-fade-up">
          <div className="flex items-center gap-2 text-forest-600 mb-2">
            <ShieldCheck className="h-4 w-4" />
            <span className="font-mono text-xs uppercase tracking-[0.18em]">Private & confidential</span>
          </div>
          <h1 className="font-serif text-4xl font-semibold text-forest-900">Create your account</h1>
          <p className="text-ink-500 mt-2">Save your case details privately and get tailored guidance.</p>

          <form onSubmit={submit} className="mt-8 space-y-4">
            <div>
              <Label htmlFor="name" className="text-ink-700">Full name</Label>
              <Input id="name" data-testid="register-name" required value={name}
                onChange={(e) => setName(e.target.value)} className="mt-1.5 rounded-xl border-parchment-200 bg-white h-11" placeholder="Jordan Smith" />
            </div>
            <div>
              <Label htmlFor="email" className="text-ink-700">Email</Label>
              <Input id="email" data-testid="register-email" type="email" required value={email}
                onChange={(e) => setEmail(e.target.value)} className="mt-1.5 rounded-xl border-parchment-200 bg-white h-11" placeholder="you@example.com" />
            </div>
            <div>
              <Label htmlFor="password" className="text-ink-700">Password</Label>
              <Input id="password" data-testid="register-password" type="password" required minLength={6} value={password}
                onChange={(e) => setPassword(e.target.value)} className="mt-1.5 rounded-xl border-parchment-200 bg-white h-11" placeholder="At least 6 characters" />
            </div>
            {error && <p data-testid="register-error" className="text-sm text-destructive">{error}</p>}
            <Button type="submit" data-testid="register-submit" disabled={loading}
              className="w-full rounded-xl h-11 bg-forest-800 hover:bg-forest-900 text-parchment-50">
              {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Create account"}
            </Button>
            <p data-testid="register-legal-consent" className="text-xs text-ink-400 text-center">
              By creating an account you agree to our <Link to="/terms" className="underline hover:text-terracotta">Terms</Link> and <Link to="/privacy" className="underline hover:text-terracotta">Privacy Policy</Link>.
            </p>
          </form>

          <div className="my-6 flex items-center gap-3 text-ink-400 text-xs">
            <div className="h-px flex-1 bg-parchment-200" /> OR <div className="h-px flex-1 bg-parchment-200" />
          </div>

          <Button onClick={googleLogin} data-testid="register-google" variant="outline"
            className="w-full rounded-xl h-11 border-parchment-200 bg-white gap-2 text-ink-700">
            <img src="https://www.svgrepo.com/show/475656/google-color.svg" alt="" className="h-4 w-4" />
            Continue with Google
          </Button>

          <p className="mt-6 text-sm text-ink-500 text-center">
            Already have an account? <Link to="/login" data-testid="register-to-login" className="text-terracotta font-medium hover:underline">Log in</Link>
          </p>
        </div>
      </div>

      <div className="hidden lg:block relative bg-forest-900 order-1 lg:order-2">
        <img
          src="https://images.unsplash.com/photo-1507537362848-9c7e70b7b5c1?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200"
          alt="Legal consultation" className="absolute inset-0 h-full w-full object-cover opacity-30" />
        <div className="relative h-full flex flex-col justify-between p-12 text-parchment-50">
          <div className="flex items-center gap-2.5">
            <Logo className="h-10 w-10" light />
            <span className="font-serif text-2xl font-semibold">SentinelGuide</span>
          </div>
          <div>
            <h2 className="font-serif text-4xl font-semibold leading-tight">Knowledge is the first step toward calm.</h2>
            <p className="mt-4 text-parchment-200/80 max-w-md">Understand every stage of your case, know your rights, and walk into court prepared.</p>
          </div>
        </div>
      </div>
    </div>
  );
}
